import React, { useState } from 'react';
import {
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';

const CancelTimeslotModal = ({ onConfirm, showModal, MODAL_TYPE, cancellationReasons = [] }) => {
    const { t } = useTranslation();
    const [selectedIdx, setSelectedIdx] = useState(null);
    const [customReason, setCustomReason] = useState('');
    const [loading, setLoading] = useState(false);

    const selectedPreset = selectedIdx != null ? cancellationReasons[selectedIdx] : null;

    const closeAction = () => showModal(MODAL_TYPE.NONE);

    const handleChipClick = (idx) => {
        setSelectedIdx(selectedIdx === idx ? null : idx);
        setCustomReason('');
    };

    const handleCustomChange = (e) => {
        setCustomReason(e.target.value);
        setSelectedIdx(null);
    };

    const isValid = selectedPreset != null || customReason.trim().length > 0;

    const handleConfirm = async () => {
        if (!isValid) return;
        setLoading(true);
        try {
            const value = selectedPreset
                ? JSON.stringify({
                      title: selectedPreset.title,
                      subtext: selectedPreset.subtext,
                      color: selectedPreset.color ?? null,
                  })
                : customReason.trim();
            await onConfirm(value);
            closeAction();
        } catch {
            // onConfirm surfaces its own error toast; keep the modal open for retry
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open onClose={closeAction} maxWidth="sm" fullWidth>
            <DialogTitle>{t('timeslots.cancelModal.title')}</DialogTitle>
            <DialogContent>
                {cancellationReasons.length > 0 && (
                    <Box sx={{ mb: 2, mt: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('bookings.cancellationReasonLabel')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {cancellationReasons.map((preset, idx) => (
                                <Chip
                                    key={idx}
                                    label={preset.title}
                                    onClick={() => handleChipClick(idx)}
                                    color={selectedIdx === idx ? 'primary' : 'default'}
                                    variant={selectedIdx === idx ? 'filled' : 'outlined'}
                                />
                            ))}
                        </Box>
                        {selectedPreset?.subtext && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {selectedPreset.subtext}
                            </Typography>
                        )}
                    </Box>
                )}
                <TextField
                    autoFocus={cancellationReasons.length === 0}
                    fullWidth
                    multiline
                    minRows={3}
                    label={t('timeslots.cancelModal.customReasonLabel')}
                    value={customReason}
                    onChange={handleCustomChange}
                    margin="dense"
                    disabled={selectedIdx !== null}
                    placeholder={selectedPreset ? selectedPreset.title : ''}
                />
            </DialogContent>
            <DialogActions>
                <CommonButton
                    buttonType="secondary"
                    onClick={closeAction}
                    disabled={loading}
                    label={t('common.cancel')}
                />
                <CommonButton
                    buttonType="primary"
                    onClick={handleConfirm}
                    disabled={loading || !isValid}
                    label={t('timeslots.cancelModal.confirm')}
                />
            </DialogActions>
        </Dialog>
    );
};

export default CancelTimeslotModal;
